<script setup lang="ts">
import RlMinerModelSelect from '@/components/common/RlMinerModelSelect.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { useDowntimes } from '@/composables/eos/downtime'
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import { exportMinersDowntimeSummary, getMinerDowntimeDetails } from '@/services/eos/downtimes'
import { SiteType } from '@/types/core/Site'
import type { IMinerDowntimeDetail, IMinersDowntimeSummary } from '@/types/eos/DowntimeEvent'
import type { IMinersDowntimeSummaryExportFilters } from '@/types/eos/filters'
import type { IHostingClient } from '@/types/eos/SiteMap'
import type { IMinerModel } from '@/types/rc/miners'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadFile } from '@/util'
import { formatHashrateToTerahashValue, tableColumnPercentageWithTwoDecimals } from '@/util/formatters'
import { Icon } from '@iconify/vue'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const props = defineProps<{
  startDate: string
  endDate: string
}>()
const isLoadingExport = ref(false)
const isLoadingHourlyExport = ref(false)
const selectedTags = ref<string[]>([])

const filters = reactive<IMinersDowntimeSummaryExportFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[0],
  site_id: undefined,
  name: undefined,
  hosting_client_id: undefined,
  tags: undefined,
  start_datetime: props.startDate || DateTime.now().startOf('month').toISO(),
  end_datetime: props.endDate || DateTime.now().endOf('month').toISO(),
  type: 'period',
})

// Watch for prop changes and update URL
watch(
  () => [props.startDate, props.endDate],
  ([newStartDate, newEndDate]) => {
    if (newStartDate && newEndDate) {
      filters.start_datetime = newStartDate
      filters.end_datetime = newEndDate

      // Clean miner expanded data and close all expanded rows
      expandedMinerData.value = {}
      expandedLoading.value = {}
      closeAllExpandedRows()

      // Update URL query parameters
      router.replace({
        query: {
          ...router.currentRoute.value.query,
          start_date: newStartDate,
          end_date: newEndDate,
        },
      })
    }
  }
)

const { getMinersDowntimeSummaryQuery } = useDowntimes()

const { isFetching, isError, isRefetching, isPending, data, error, refetch } = getMinersDowntimeSummaryQuery(filters)

// Manage table expansion to fetch miner downtime details
const minersTableRef = ref()
const expandedMinerData = ref<Record<string, IMinerDowntimeDetail[]>>({})
const expandedLoading = ref<Record<string, boolean>>({})

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  style: 'width: 200px',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const onSiteFilterUpdate = (value?: ISiteIdentifier) => {
  filters.site_id = value?.site_id ?? undefined
}

const onHostingClientFilterUpdate = (value?: IHostingClient) => {
  filters.hosting_client_id = value?.id
}

const onMinerModelFilterUpdate = (value?: IMinerModel[]) => {
  // Generate a comma separated list of miner model names
  filters.models = value?.map((res: IMinerModel) => res.name).join(',')
}

const onTagFilterUpdate = (value?: string[]) => {
  if (!value || value.length === 0) {
    filters.tags = undefined
    return
  }
  filters.tags = value.join(',')
}

const handleExportPeriodSummary = async () => {
  isLoadingExport.value = true
  try {
    filters.type = 'period'
    downloadFile((await exportMinersDowntimeSummary(filters)) as Blob, `miners_downtime_summary_export.csv`)
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Unknown error occurred'
    ElMessage.error(message)
  } finally {
    isLoadingExport.value = false
  }
}

const handleExportHourlySummary = async () => {
  isLoadingHourlyExport.value = true
  try {
    filters.type = 'hourly'
    await exportMinersDowntimeSummary(filters)
    ElMessage.success('The reports are being generated and will be sent to your email.')
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Unknown error occurred'
    ElMessage.error(message)
  } finally {
    isLoadingHourlyExport.value = false
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fetchMinerDowntimeDetails = async (row: any, _: IMinersDowntimeSummary[] | boolean) => {
  if (!expandedMinerData.value[row.id]) {
    expandedLoading.value[row.id] = true
    try {
      const details = await getMinerDowntimeDetails(row.id, {
        start_datetime: filters.start_datetime,
        end_datetime: filters.end_datetime,
      })
      expandedMinerData.value[row.id] = details
    } catch (e: unknown) {
      ElMessage.error(`Failed to load details: ${e instanceof Error ? e.message : 'Unknown error occurred'}`)
      expandedMinerData.value[row.id] = []
    } finally {
      expandedLoading.value[row.id] = false
    }
  }
}

const closeAllExpandedRows = () => {
  if (minersTableRef.value) {
    data.value?.data.forEach((row) => {
      minersTableRef.value.toggleRowExpansion(row, false)
    })
  }
}
</script>

<template>
  <rl-error-result v-if="isError && !isRefetching" :error="error">
    <el-button @click="refetch()">
      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
    </el-button>
  </rl-error-result>
  <template v-else>
    <div class="flex justify-between">
      <div class="flex gap-2">
        <RcSiteSelect
          placeholder="Site ID or Name"
          :type="SiteType.DataCenter"
          :disabled="isPending"
          style="width: 200px"
          @update:model-value="onSiteFilterUpdate"
        />
        <EosHostingClientSelect
          placeholder="Hosting Client"
          :disabled="isPending"
          style="width: 200px"
          @update:model-value="onHostingClientFilterUpdate"
        />
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
        <el-select-v2
          v-model="selectedTags"
          placeholder="Tags"
          :disabled="isPending"
          clearable
          multiple
          filterable
          allow-create
          :show-arrow="false"
          collapse-tags
          no-data-text="Type the tags..."
          collapse-tags-tooltip
          :max-collapse-tags="0"
          style="width: 200px"
          :options="[]"
          @change="onTagFilterUpdate"
        />
        <RlMinerModelSelect
          style="width: 280px"
          placeholder="Miner Models"
          :disabled="isPending"
          @change="onMinerModelFilterUpdate"
        />
      </div>
      <el-dropdown
        trigger="hover"
        @command="(cmd: string) => (cmd === 'period' ? handleExportPeriodSummary() : handleExportHourlySummary())"
      >
        <el-button type="primary" :loading="isLoadingExport || isLoadingHourlyExport">
          Export
          <Icon icon="material-symbols:arrow-drop-down" class="ml-1" height="20" width="20" />
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="period">Period Summary</el-dropdown-item>
            <el-dropdown-item command="hourly">Hourly Summary</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <el-table
      ref="minersTableRef"
      v-loading="isFetching"
      :data="data?.data"
      empty-text="No data"
      stripe
      @expand-change="fetchMinerDowntimeDetails"
    >
      <el-table-column type="expand" width="50">
        <template #default="scope">
          <el-table
            v-loading="expandedLoading[scope.row.id]"
            :data="expandedMinerData[scope.row.id]"
            empty-text="No data"
            stripe
            size="small"
          >
            <el-table-column label="Downtimes" header-align="center">
              <el-table-column
                prop="downtime_type"
                label="Downtime Type"
                min-width="150"
                :formatter="
                  (row: IMinerDowntimeDetail) => `${row.downtime_type.type_category} - ${row.downtime_type.type_name}`
                "
              />
              <el-table-column prop="for_hour_utc" label="Hour" min-width="150" :formatter="dateTimeFormatter" />
              <el-table-column prop="minutes" label="Minutes" min-width="50" />
              <el-table-column prop="site.name" label="Site" min-width="120" />
              <el-table-column prop="structure.name" label="Structure" min-width="120" />
              <el-table-column prop="rack.name" label="Rack" min-width="120" />
            </el-table-column>
          </el-table>
        </template>
      </el-table-column>
      <el-table-column prop="name" label="Miner Name" :min-width="196" />
      <el-table-column prop="model" label="Model" :min-width="196" />
      <el-table-column prop="tags" label="Tags" :min-width="196">
        <template #default="scope">
          <div class="flex flex-wrap gap-2">
            <el-tag v-for="tag in scope.row.tags" :key="tag">{{ tag }}</el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="hosting_client" label="Hosting Client" :min-width="196" />
      <el-table-column prop="general_downtime_minutes" label="General Downtime Minutes" :min-width="120" />
      <el-table-column prop="derack_downtime_minutes" label="Derack Downtime Minutes" :min-width="120" />
      <el-table-column prop="last_hashrate_value" label="Last Hashrate (TH/s)" :min-width="120">
        <template #default="scope">
          <el-tooltip
            v-if="scope.row.last_hashrate_timestamp"
            placement="top"
            :content="`Measured at ${dateTimeFormatter(scope.row, null, scope.row.last_hashrate_timestamp)}`"
          >
            <span class="cursor-default">{{ formatHashrateToTerahashValue(scope.row.last_hashrate_value) }}</span>
          </el-tooltip>
          <span v-else>{{ formatHashrateToTerahashValue(scope.row.last_hashrate_value) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        prop="period_uptime_percentage"
        label="Period Uptime"
        :min-width="120"
        :formatter="tableColumnPercentageWithTwoDecimals"
      />
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      :disabled="isRefetching"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      background
      class="self-end"
    />
  </template>
</template>
